import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { readableNumber } from '@attain-apps/string-utils'

/** Formats table numbers and masks values that overflow their cell. */
@ngCore.Component({
  selector: 'atn-value-number',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component implements ngCore.OnDestroy {
  /** Visible number element used for the overflow comparison. */
  public readonly numberElSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLDivElement>>('number')

  /** Hidden element used to measure the untruncated formatted value. */
  public readonly hiddenElSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLDivElement>>('hidden')

  /** Measured width of the untruncated formatted value. */
  private readonly _hiddenElScrollWidthSg = ngCore.signal<number | undefined>(
    undefined,
  )

  /** Measured width available to the visible formatted value. */
  private readonly _numberElClientWidthSg = ngCore.signal<number | undefined>(
    undefined,
  )

  /** Rechecks the visible and hidden widths whenever the cell is resized. */
  private readonly _resizeObserver = new ResizeObserver(() => {
    const numberEl = this.numberElSg()?.nativeElement
    const hiddenEl = this.hiddenElSg()?.nativeElement
    if (numberEl) {
      this._numberElClientWidthSg.set(numberEl.clientWidth)
    }

    if (hiddenEl) {
      this._hiddenElScrollWidthSg.set(hiddenEl.scrollWidth)
    }
  })

  /** Number value to format. */
  public readonly valueSg = ngCore.input<
    ents.core.common.value.Raw | undefined
  >(undefined, {
    alias: 'value',
  })

  /** Metadata used to select the number format. */
  public readonly valueInfoSg = ngCore.input<
    ents.core.common.value.Info | undefined
  >(undefined, {
    alias: 'valueInfo',
  })

  /** Whether the formatted value is wider than the visible cell. */
  public readonly isOverflowingSg = ngCore.computed(
    (
      hiddenDivScrollWidth = this._hiddenElScrollWidthSg(),
      numberDivClientWidth = this._numberElClientWidthSg(),
    ) => (hiddenDivScrollWidth ?? 0) > (numberDivClientWidth ?? 0),
  )

  /** Formatted number displayed in the table cell. */
  public readonly readableNumberSg = ngCore.computed(
    (value = this.valueSg(), valueInfo = this.valueInfoSg()) => {
      if (value === undefined || typeof value !== 'number') {
        return undefined
      }

      const valueType =
        valueInfo?.type ??
        ents.core.common.value.types.Intrinsic.inferFrom(value)
      const preset =
        ents.core.common.value.types.Intrinsic.ForNumber.isForNumber(valueType)
          ? ents.core.common.value.types.Intrinsic.ForNumber.toReadableNumberPreset(
              valueType,
            )
          : undefined

      return readableNumber(value, preset ?? { decimals: valueInfo?.precision })
    },
  )

  /** Placeholder shown while the formatted value does not fit. */
  public readonly overflowPlaceholderSg = ngCore.computed(
    (_readableNumber = this.readableNumberSg()) =>
      _readableNumber?.replace(/./g, '#'),
  )

  constructor() {
    ngCore.effect(() => {
      const numberEl = this.numberElSg()?.nativeElement
      if (numberEl) {
        this._resizeObserver.observe(numberEl)
      }
    })
  }

  /** Disconnects the resize observer when the cell is destroyed. */
  ngOnDestroy(): void {
    this._resizeObserver.disconnect()
  }
}
